<template>
  <button
    v-if="isAdmin && notificationSupported && notificationPermission === 'default' && !visible"
    type="button"
    class="fixed bottom-4 left-4 z-[140] rounded-full bg-sky-700 px-4 py-2 text-xs font-semibold text-white shadow-lg hover:bg-sky-800"
    @click="requestDesktopNotifications"
  >
    <i class="fa-solid fa-bell mr-1"></i>
    Arama bildirimlerini aç
  </button>

  <aside
    v-if="visible && isAdmin"
    class="fixed bottom-4 left-4 z-[140] w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-xl border border-sky-200 bg-white shadow-2xl"
    aria-live="polite"
  >
    <div class="flex items-center justify-between bg-sky-700 px-4 py-3 text-white">
      <div class="min-w-0">
        <p class="truncate text-sm font-semibold mb-0">{{ eventTitle }}</p>
        <p class="text-xs text-sky-100 mb-0">
          {{ extensionSummary }}
          <span v-if="eventTimeLabel"> · {{ eventTimeLabel }}</span>
        </p>
      </div>
      <div class="ml-3 flex items-center gap-2">
        <button class="rounded px-2 py-1 text-xs hover:bg-white/15" type="button" @click="collapsed = !collapsed">
          {{ collapsed ? 'Aç' : 'Küçült' }}
        </button>
        <button class="rounded px-2 py-1 text-lg leading-none hover:bg-white/15" type="button" aria-label="Kapat" @click="closePopup">×</button>
      </div>
    </div>

    <div v-if="!collapsed" class="max-h-[78vh] space-y-3 overflow-y-auto p-4 text-sm text-slate-700">
      <div v-if="callEvents.length" class="flex flex-wrap gap-1.5" aria-label="Çağrı zaman çizelgesi">
        <span
          v-for="item in callEvents"
          :key="item.key"
          class="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600"
        >
          {{ getCallEventLabel(item.type) }} · {{ formatCallEventTime(item.event_time) }}
        </span>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0">Görüşülen numara</p>
        <a v-if="call.call_number" :href="`tel:${call.call_number}`" class="font-semibold text-sky-700 hover:underline">{{ call.call_number }}</a>
        <span v-else>Numara alınamadı</span>
        <p v-if="call.destination_number" class="mt-1 text-xs text-slate-500">Hedef hat: {{ call.destination_number }}</p>
      </div>

      <section v-if="detailAuthorized" class="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
        <div class="flex items-center justify-between gap-2">
          <p class="mb-0 text-xs font-semibold uppercase tracking-wide text-emerald-800">Cevaplayan kullanıcı detayları</p>
          <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Yalnızca siz</span>
        </div>

        <p v-if="callDetails.detail_error" class="mb-0 rounded bg-amber-50 p-2 text-xs text-amber-800">
          Detaylar şu anda yüklenemedi; kişi kartından erişebilirsiniz.
        </p>

        <div>
          <p class="mb-1 text-xs font-semibold text-slate-700">Rezervasyonlar</p>
          <div v-if="upcomingReservations.length" class="space-y-1.5">
            <p class="mb-0 text-[10px] font-semibold uppercase text-emerald-700">Yaklaşan</p>
            <NuxtLink
              v-for="reservation in upcomingReservations"
              :key="`upcoming-${reservation.id}`"
              :to="`/admin/reservations/${encodeURIComponent(reservation.id)}`"
              class="block rounded-md border border-emerald-100 bg-white p-2 hover:border-emerald-300"
            >
              <span class="font-semibold text-slate-800">#{{ reservation.id }} · {{ reservation.title }}</span>
              <span class="mt-0.5 block text-[11px] text-slate-500">{{ reservationDateLabel(reservation) }} · {{ reservationStatusLabel(reservation.status) }}</span>
            </NuxtLink>
          </div>
          <div v-if="pastReservations.length" class="mt-2 space-y-1.5">
            <p class="mb-0 text-[10px] font-semibold uppercase text-slate-500">Geçmiş</p>
            <NuxtLink
              v-for="reservation in pastReservations"
              :key="`past-${reservation.id}`"
              :to="`/admin/reservations/${encodeURIComponent(reservation.id)}`"
              class="block rounded-md border border-slate-200 bg-white p-2 hover:border-sky-300"
            >
              <span class="font-semibold text-slate-800">#{{ reservation.id }} · {{ reservation.title }}</span>
              <span class="mt-0.5 block text-[11px] text-slate-500">{{ reservationDateLabel(reservation) }} · {{ reservationStatusLabel(reservation.status) }}</span>
            </NuxtLink>
          </div>
          <p v-if="!upcomingReservations.length && !pastReservations.length" class="mb-0 rounded bg-white p-2 text-xs text-slate-500">
            Kayıtlı rezervasyon bulunamadı.
          </p>
        </div>

        <div>
          <p class="mb-1 text-xs font-semibold text-slate-700">Son {{ whatsappWindowDays }} günlük WhatsApp</p>
          <div v-if="whatsappMessages.length" class="space-y-1.5">
            <div v-for="message in whatsappMessages" :key="message.id" class="rounded-md border border-slate-200 bg-white p-2">
              <div class="flex items-center justify-between gap-2 text-[10px]">
                <span :class="message.direction === 'incoming' ? 'font-semibold text-emerald-700' : 'font-semibold text-sky-700'">
                  {{ message.direction === 'incoming' ? 'Gelen' : 'Gönderilen' }}
                </span>
                <span class="text-slate-400">{{ formatCallEventTime(message.created_at) }}</span>
              </div>
              <p class="mb-0 mt-1 line-clamp-2 text-xs text-slate-700">{{ message.message }}</p>
            </div>
          </div>
          <p v-else class="mb-0 rounded bg-white p-2 text-xs text-slate-500">Yakın zamanda WhatsApp mesajı yok.</p>
        </div>
      </section>

      <div class="flex gap-3 rounded-lg bg-slate-50 p-3">
        <img
          v-if="personPhoto"
          :src="personPhoto"
          :alt="personName || 'Yolcu fotoğrafı'"
          class="h-20 w-20 shrink-0 rounded-xl border border-slate-200 bg-white object-cover"
        />
        <div
          v-else
          class="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-2xl text-slate-300"
          aria-hidden="true"
        >
          <i class="fa-solid fa-user"></i>
        </div>
        <div class="min-w-0 flex-1">
        <p class="text-xs font-medium uppercase tracking-wide text-slate-400 mb-0">Kişi kartı</p>
        <p class="mt-1 font-semibold text-slate-900">{{ personName || 'Kayıtlı kişi bulunamadı' }}</p>
        <p v-if="call.pers_mobile" class="mt-1 text-xs">{{ call.pers_mobile }}</p>
        <p v-if="call.pers_email" class="truncate text-xs">{{ call.pers_email }}</p>
        <p v-if="call.pers_tckn" class="mt-1 text-xs text-slate-500">T.C.: {{ call.pers_tckn }}</p>
        <NuxtLink
          v-if="call.call_number"
          :to="personPath(call)"
          class="mt-2 inline-block text-xs font-medium text-sky-700 hover:underline"
        >
          Kişi kartını aç
        </NuxtLink>
        </div>
      </div>

      <div v-if="notificationSupported && notificationPermission !== 'granted'" class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <button
          v-if="notificationPermission === 'default'"
          type="button"
          class="font-semibold underline underline-offset-2"
          @click="requestDesktopNotifications"
        >
          Site arkadayken masaüstü bildirimlerini aç
        </button>
        <p v-else class="mb-0">Masaüstü bildirimleri tarayıcı ayarlarından engellenmiş.</p>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  callWasAnswered,
  getAnsweredExtension,
  getAnswererLabel,
  getCallEventRank,
} from '~/utils/callAnswering'
import {
  buildDesktopCallNotification,
  formatCallEventTime,
  getCallEventLabel,
  getCallPersonName,
  shouldShowDesktopCallNotification,
} from '~/utils/callNotification'

const auth = useUser() as Ref<Record<string, any> | null>
const { $socket } = useNuxtApp() as { $socket?: any }
const visible = ref(false)
const collapsed = ref(false)
const call = ref<Record<string, string>>({})
const callEvents = ref<Array<Record<string, string>>>([])
const callDetails = ref<Record<string, any>>({})
const detailAuthorized = ref(false)
const notificationSupported = ref(false)
const notificationPermission = ref<NotificationPermission>('default')
const notifiedCalls = new Set<string>()
const router = useRouter()
const { showImage } = useCommon()
const AUTO_COLLAPSE_MS = 10_000
const NON_ANSWERER_CLOSE_MS = 3_500
let collapseTimer: ReturnType<typeof setTimeout> | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null

const isAdmin = computed(() => auth.value?.U_Admin === 'on')
const personName = computed(() => getCallPersonName(call.value))
const personPhoto = computed(() => call.value.person_photo
  ? showImage(call.value.person_photo, 160, 160, true, 85, 'jpg', 'no-upscale')
  : '')
const currentUserExtension = computed(() => String(auth.value?.U_Dial || '').replace(/\D/g, ''))
const eventTimeLabel = computed(() => formatCallEventTime(call.value.event_time))
const upcomingReservations = computed(() => callDetails.value.reservations?.upcoming || [])
const pastReservations = computed(() => callDetails.value.reservations?.past || [])
const whatsappMessages = computed(() => callDetails.value.whatsapp_messages || [])
const whatsappWindowDays = computed(() => callDetails.value.whatsapp_window_days || 30)
const personPath = (event: Record<string, string>) => {
  const identifier = event.call_persona_id || event.call_number
  if (!identifier) return '/admin/dashboard'
  return `/admin/user/${encodeURIComponent(identifier)}?mobile=${encodeURIComponent(event.call_number || '')}`
}
const eventTitle = computed(() => ({
  ringing: 'Gelen arama',
  answer: 'Görüşme başladı',
  hangup: 'Görüşme sonlandı',
  user_hangup: 'Görüşme sonlandı',
}[call.value.type] || 'Telefon olayı'))
const extensionSummary = computed(() => {
  if (call.value.type === 'ringing') {
    const extension = call.value.ringing_extension
    return extension ? `Çalan dahili: ${extension}` : 'Çalan dahili belirlenemedi'
  }
  if (call.value.type === 'answer') return `Cevaplayan: ${getAnswererLabel(call.value)}`
  if (['hangup', 'user_hangup'].includes(call.value.type)) {
    return callWasAnswered(call.value)
      ? `Cevaplayan: ${getAnswererLabel(call.value)}`
      : 'Cevapsız çağrı'
  }
  const extension = getAnsweredExtension(call.value)
  return extension ? `Cevaplayan: ${getAnswererLabel(call.value)}` : 'Çağrı bilgisi'
})

const reservationDateLabel = (reservation: Record<string, string>) => {
  const start = String(reservation.start_date || '').slice(0, 10)
  const end = String(reservation.end_date || '').slice(0, 10)
  return [start, end].filter(Boolean).join(' → ') || 'Tarih belirtilmemiş'
}

const reservationStatusLabel = (status: string) => ({
  SD_Confirmed: 'Onaylı',
  SD_Waiting: 'Bekliyor',
  SD_Cancelled: 'İptal',
}[status] || status || 'Durum belirtilmemiş')

const isCurrentUserAnswerer = (event: Record<string, any>) => {
  const answeredExtension = String(event.answered_by_extension || getAnsweredExtension(event) || '').replace(/\D/g, '')
  return Boolean(answeredExtension && currentUserExtension.value && answeredExtension === currentUserExtension.value)
}

const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
  if (timer) clearTimeout(timer)
}

const scheduleCollapse = () => {
  clearTimer(collapseTimer)
  collapseTimer = setTimeout(() => {
    if (visible.value) collapsed.value = true
  }, AUTO_COLLAPSE_MS)
}

const scheduleCloseForNonAnswerer = () => {
  clearTimer(closeTimer)
  closeTimer = setTimeout(() => {
    visible.value = false
  }, NON_ANSWERER_CLOSE_MS)
}

const closePopup = () => {
  visible.value = false
  clearTimer(collapseTimer)
  clearTimer(closeTimer)
}

const rememberCallEvent = (event: Record<string, string>) => {
  const eventTime = event.event_time || new Date().toISOString()
  const key = `${event.type || 'event'}:${eventTime}`
  if (callEvents.value.some((item) => item.key === key)) return
  callEvents.value.push({ key, type: event.type || '', event_time: eventTime })
}

const requestDesktopNotifications = async () => {
  if (!notificationSupported.value) return
  notificationPermission.value = await Notification.requestPermission()
}

const claimNotification = (event: Record<string, string>) => {
  const callKey = event.call_uuid || event.call_number
  if (!callKey || notifiedCalls.has(callKey)) return false
  const storageKey = `inanctur:call-notification:${callKey}`
  const now = Date.now()
  try {
    const lastShownAt = Number(localStorage.getItem(storageKey) || 0)
    if (lastShownAt && now - lastShownAt < 60_000) return false
    localStorage.setItem(storageKey, String(now))
  } catch {
    // The in-memory set still prevents repeats when storage is unavailable.
  }
  notifiedCalls.add(callKey)
  if (notifiedCalls.size > 100) notifiedCalls.delete(notifiedCalls.values().next().value || '')
  return true
}

const showDesktopNotification = (event: Record<string, string>) => {
  if (!notificationSupported.value) return
  notificationPermission.value = Notification.permission
  if (!shouldShowDesktopCallNotification({
    eventType: event.type,
    permission: notificationPermission.value,
    documentHidden: document.hidden,
    windowFocused: document.hasFocus(),
  }) || !claimNotification(event)) return

  const content = buildDesktopCallNotification(event)
  const notification = new Notification(content.title, {
    body: content.body,
    tag: content.tag,
    icon: event.person_photo
      ? showImage(event.person_photo, 192, 192, true, 85, 'png', 'no-upscale')
      : '/favicon.png',
    requireInteraction: true,
  })
  notification.onclick = () => {
    window.focus()
    notification.close()
    router.push(personPath(event))
  }
}

const receiveCall = (event: Record<string, string>) => {
  if (!isAdmin.value) return
  const sameCall = Boolean(event.call_uuid && event.call_uuid === call.value.call_uuid)
  if (sameCall && getCallEventRank(event.type) < getCallEventRank(call.value.type)) return
  if (!sameCall) {
    clearTimer(closeTimer)
    callEvents.value = []
    callDetails.value = {}
    detailAuthorized.value = false
  }
  const nextCall = sameCall ? { ...call.value, ...event } : event
  call.value = nextCall
  rememberCallEvent(event)

  const answeredElsewhere = Boolean(getAnsweredExtension(nextCall) && !isCurrentUserAnswerer(nextCall))
  if (answeredElsewhere && ['hangup', 'user_hangup'].includes(event.type)) {
    scheduleCloseForNonAnswerer()
    return
  }

  collapsed.value = false
  visible.value = true
  showDesktopNotification(nextCall)
  scheduleCollapse()
  if (event.type === 'answer' && answeredElsewhere) scheduleCloseForNonAnswerer()
}

const receiveCallDetail = (details: Record<string, any>) => {
  if (!isAdmin.value || details.call_uuid !== call.value.call_uuid || !details.detail_authorized) return
  if (!isCurrentUserAnswerer(details)) return
  clearTimer(closeTimer)
  callDetails.value = details
  detailAuthorized.value = true
  collapsed.value = false
  visible.value = true
  scheduleCollapse()
}

onMounted(() => {
  notificationSupported.value = 'Notification' in window
  if (notificationSupported.value) notificationPermission.value = Notification.permission
  $socket?.on('call-event', receiveCall)
  $socket?.on('call-detail', receiveCallDetail)
})
onBeforeUnmount(() => {
  clearTimer(collapseTimer)
  clearTimer(closeTimer)
  $socket?.off('call-event', receiveCall)
  $socket?.off('call-detail', receiveCallDetail)
})
</script>
